import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api.ts';

interface User {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  user_type: 'customer' | 'trainer' | 'admin';
  date_joined: string;
  is_active: boolean;
}

interface Post {
  id: string;
  content: string;
  author: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
  };
  created_at: string;
  likes_count: number;
  comments_count: number;
}

export const AdminPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'users' | 'posts' | 'create-trainer'>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [newTrainer, setNewTrainer] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    bio: '',
    specializations: '',
    years_experience: 0,
    hourly_rate: 0,
    rate_30_min: 0,
    certifications: ''
  });

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'posts') {
      fetchPosts();
    }
  }, [activeTab]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getAllUsers();
      setUsers(response.data.users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getAllPosts();
      setPosts(response.data.posts);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string, username: string) => {
    if (window.confirm(`Are you sure you want to deactivate user "${username}"? This action cannot be undone.`)) {
      try {
        await adminAPI.deleteUser(userId);
        setUsers(users.map(user => 
          user.id === userId ? { ...user, is_active: false } : user
        ));
        alert('User deactivated successfully');
      } catch (error) {
        console.error('Failed to deactivate user:', error);
        alert('Failed to deactivate user');
      }
    }
  };

  const handleDeletePost = async (postId: string) => {
    if (window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      try {
        await adminAPI.deletePost(postId);
        setPosts(posts.filter(post => post.id !== postId));
        alert('Post deleted successfully');
      } catch (error) {
        console.error('Failed to delete post:', error);
        alert('Failed to delete post');
      }
    }
  };

  const handleCreateTrainer = async () => {
  setLoading(true);
  try {
    console.log('Sending trainer data:', newTrainer);
    const response = await adminAPI.createTrainer(newTrainer);  // Capture response here
    console.log('Response:', response);
    alert('Trainer created successfully');
    setNewTrainer({
      username: '',
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      bio: '',
      specializations: '',
      years_experience: 0,
      hourly_rate: 0,
      rate_30_min: 0,
      certifications: ''
    });
    if (activeTab === 'users') {
      fetchUsers();
    }
  } catch (error: any) {
    console.error('Failed to create trainer:', error);
    console.error('Error response:', error.response?.data);
    alert('Failed to create trainer: ' + (error.response?.data?.error || error.message));
  } finally {
    setLoading(false);
  }
};

  const getUserTypeColor = (userType: string) => {
    switch (userType) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'trainer': return 'bg-blue-100 text-blue-800';
      case 'customer': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Panel</h1>
        <p className="text-gray-600">Manage users, posts, and create new trainers</p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'users', label: 'Users Management', icon: '👥' },
            { id: 'posts', label: 'Posts Management', icon: '📝' },
            { id: 'create-trainer', label: 'Create Trainer', icon: '➕' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Users Management Tab */}
      {activeTab === 'users' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Users Management</h2>
            <button
              onClick={fetchUsers}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">Loading users...</div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {users.map((user) => (
                  <li key={user.id} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div>
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">
                              {user.first_name} {user.last_name}
                            </p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getUserTypeColor(user.user_type)}`}>
                              {user.user_type}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">@{user.username} • {user.email}</p>
                          <p className="text-xs text-gray-400">Joined: {new Date(user.date_joined).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {user.user_type !== 'admin' && user.is_active && (
                          <button
                            onClick={() => handleDeleteUser(user.id, user.username)}
                            className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                          >
                            Deactivate
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Posts Management Tab */}
      {activeTab === 'posts' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Posts Management</h2>
            <button
              onClick={fetchPosts}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">Loading posts...</div>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => (
                <div key={post.id} className="bg-white p-6 rounded-lg shadow border">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="font-medium text-gray-900">
                        {post.author.first_name} {post.author.last_name}
                      </p>
                      <p className="text-sm text-gray-500">@{post.author.username}</p>
                      <p className="text-xs text-gray-400">{new Date(post.created_at).toLocaleString()}</p>
                    </div>
                    <button
                      onClick={() => handleDeletePost(post.id)}
                      className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                    >
                      Delete Post
                    </button>
                  </div>
                  <p className="text-gray-800 mb-4">{post.content}</p>
                  <div className="flex space-x-4 text-sm text-gray-500">
                    <span>👍 {post.likes_count} likes</span>
                    <span>💬 {post.comments_count} comments</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Trainer Tab */}
      {activeTab === 'create-trainer' && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Create New Trainer</h2>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                <input
                  type="text"
                  required
                  value={newTrainer.username}
                  onChange={(e) => setNewTrainer({...newTrainer, username: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  required
                  value={newTrainer.email}
                  onChange={(e) => setNewTrainer({...newTrainer, email: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  required
                  value={newTrainer.password}
                  onChange={(e) => setNewTrainer({...newTrainer, password: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                <input
                  type="text"
                  required
                  value={newTrainer.first_name}
                  onChange={(e) => setNewTrainer({...newTrainer, first_name: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                <input
                  type="text"
                  required
                  value={newTrainer.last_name}
                  onChange={(e) => setNewTrainer({...newTrainer, last_name: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Years Experience</label>
                <input
                  type="number"
                  min="0"
                  value={newTrainer.years_experience}
                  onChange={(e) => setNewTrainer({...newTrainer, years_experience: parseInt(e.target.value)})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hourly Rate ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newTrainer.hourly_rate}
                  onChange={(e) => setNewTrainer({...newTrainer, hourly_rate: parseFloat(e.target.value)})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
               <label className="block text-sm font-medium text-gray-700 mb-2">30-Minute Rate ($)</label>
               <input
                 type="number"
                 min="0"
                 step="0.01"
                 value={newTrainer.rate_30_min}
                 onChange={(e) => setNewTrainer({...newTrainer, rate_30_min: parseFloat(e.target.value)})}
                 className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
               />
             </div>
            </div>

            {/* Full-width fields */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Bio</label>
              <textarea
                rows={3}
                value={newTrainer.bio}
                onChange={(e) => setNewTrainer({...newTrainer, bio: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Tell us about this trainer..."
              />
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Specializations</label>
              <input
                type="text"
                value={newTrainer.specializations}
                onChange={(e) => setNewTrainer({...newTrainer, specializations: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Weight Loss, Strength Training, Yoga"
              />
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Certifications</label>
              <input
                type="text"
                value={newTrainer.certifications}
                onChange={(e) => setNewTrainer({...newTrainer, certifications: e.target.value})}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., NASM-CPT, ACE, ACSM"
              />
            </div>

            <div className="mt-8">
              <button
                onClick={handleCreateTrainer}
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Creating Trainer...' : 'Create Trainer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};